import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CheckCheck, Clock } from 'lucide-react';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { formatDateTime, formatSpan } from '@/shared/lib/format';
import type { DeskQuestion } from '../api/deskApi';

/** Where the reply went: Teams once the bot has posted it, else the portal, where the RM sees it on their next look. */
function Delivery({ q }: { q: DeskQuestion }) {
  if (q.channel === 'portal') return <Typography variant="caption" sx={{ color: 'text.secondary' }}>Shown to {q.askedBy} in the portal</Typography>;
  const done = q.deliveredAt !== null;
  const Icon = done ? CheckCheck : Clock;
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, color: done ? 'success.dark' : 'text.secondary' }}>
      <Icon size={13} aria-hidden />
      <Typography variant="caption" sx={{ color: 'inherit' }}>{done ? `Delivered to ${q.askedBy} in Teams` : `Sending to ${q.askedBy} in Teams`}</Typography>
    </Stack>
  );
}

/** The analyst's reply: who, when, how long it took, the text, and where it went. */
export function DeskAnswer({ q }: { q: DeskQuestion }) {
  if (!q.answer) return null;
  const by = q.answeredBy ?? 'Research';
  return (
    <Box component="section" aria-label="Answer" sx={cardSx}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <PersonAvatar name={by} size={20} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>{by}</Box> answered
          {q.answeredAt ? ` · ${formatDateTime(q.answeredAt)} · ${formatSpan(q.createdAt, q.answeredAt)} later` : ''}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>{q.answer}</Typography>
      <Stack direction="row" sx={{ justifyContent: 'flex-end' }}><Delivery q={q} /></Stack>
    </Box>
  );
}

const cardSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  p: 2,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  '@keyframes answerIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'answerIn 260ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;
